import '../../../shared/StyledInput.css';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, X } from 'lucide-react';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import SaveButton from '../../../shared/SaveButton';
import CancelButton from '../../../shared/CancelButton';
import Accordion from '../../../shared/Accordion';
import DateInput from '../../../shared/DateInput';
import FilterDrawer from '../../../shared/FilterDrawer';
import SyncedEmailList from '../../../shared/SyncedEmailList';
import MailboxFolderPicker from '../../../shared/MailboxFolderPicker';
import { formatDate, formatDateTime, todayInTenant } from '../../../shared/tenantClock';
import { askConfirm } from '../../../shared/AppDialogs';

// The signed-in staff member's own account page (/global-my-account, issue #259),
// opened from My Account in the topbar user menu. Connects their own
// mailbox to Email Sync — on the company's shared-mailbox server, checked
// as often as the shared mailbox — and keeps their never-log list.
// Everything here is theirs alone: an admin sees only whether a mailbox is
// connected and how its sync is going (Users list), never the password or
// the never-log list.

const LABEL_STYLE = { display: 'block', fontSize: 13, color: '#5a6b85', marginBottom: 4 };
const HELP_STYLE = { fontSize: 12, color: '#888', marginTop: 4 };
const INTRO_STYLE = { fontSize: 13, color: '#5a6b85', margin: '0 0 14px' };
const LINK_BUTTON = { marginLeft: 10, background: 'none', border: 'none', padding: 0, color: '#1d4ed8', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 };
const SECONDARY_BUTTON = { padding: '7px 14px', borderRadius: 6, border: '1px solid #bfc8d6', background: '#fff', color: '#333', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 };

function frequencyLabel(minutes) {
  if (!minutes) return '';
  if (minutes < 60) return `every ${minutes} minutes`;
  if (minutes === 60) return 'every hour';
  if (minutes < 1440) return `every ${minutes / 60} hours`;
  return 'once a day';
}

// The email Activities one Sync Now logged, newest first, in a right-side
// drawer whose body scrolls however long the list gets (shared
// SyncedEmailList, also on the CRM Dashboard).
function SyncResultsDrawer({ open, onOpenChange, activities, onRemoved }) {
  return (
    <FilterDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={`Logged by this sync (${activities.length})`}
      description="Email Activities this Sync Now logged from your mailbox. Click one to open its record."
    >
      {activities.length === 0
        ? <div style={{ fontSize: 14, color: '#888' }}>Nothing left in this list.</div>
        : <SyncedEmailList activities={activities} onRemoved={onRemoved} />}
    </FilterDrawer>
  );
}

function ConnectForm({ account, onSaved }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [email, setEmail] = useState(account.email || '');
  const [password, setPassword] = useState('');
  const [historyStart, setHistoryStart] = useState(todayInTenant());
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post('/api/my-account/mailbox', { email, password, history_start_date: historyStart });
      dismissError();
      onSaved(res.data.data, res.data.message);
    } catch (err) {
      showError(err, 'Could not connect your mailbox');
    }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={INTRO_STYLE}>
        Email in your mailbox to or from a Customer, Prospect or Supplier with Email Sync on is logged as an Activity on their record, showing you as the staff member it came from. Your Inbox and Sent folders are read, plus your Archive folder and all its subfolders if you have one. You can change which folders once it's connected.
      </p>
      <div>
        <label style={LABEL_STYLE} htmlFor="my-mailbox-email">Email address</label>
        <input id="my-mailbox-email" className="styled-input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} style={{ width: '100%', maxWidth: 360 }} />
      </div>
      <div>
        <label style={LABEL_STYLE} htmlFor="my-mailbox-password">Mailbox password</label>
        <input id="my-mailbox-password" className="styled-input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} style={{ width: '100%', maxWidth: 360 }} />
        <div style={HELP_STYLE}>The password you use for this mailbox at your email provider. Some providers need an app password here.</div>
      </div>
      <div>
        <label style={LABEL_STYLE}>History start date</label>
        <DateInput value={historyStart} max={todayInTenant()} onChange={(e) => setHistoryStart(e.target.value)} style={{ width: 180 }} />
        <div style={HELP_STYLE}>The first sync goes back no further than this date.</div>
      </div>
      <div>
        <SaveButton loading={saving} disabled={!email.trim() || !password || !historyStart}>
          {saving ? 'Connecting...' : 'Connect'}
        </SaveButton>
      </div>
    </form>
  );
}

function PasswordForm({ onSaved, onCancel }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put('/api/my-account/mailbox/password', { password });
      dismissError();
      onSaved(res.data.data, res.data.message);
    } catch (err) {
      showError(err, 'Could not update your mailbox password');
    }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 14 }}>
      <label style={LABEL_STYLE} htmlFor="my-mailbox-new-password">New mailbox password</label>
      <input id="my-mailbox-new-password" autoFocus className="styled-input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} style={{ width: '100%', maxWidth: 360 }} />
      <div style={{ marginTop: 12 }}>
        <SaveButton loading={saving} disabled={!password} />
        <CancelButton onClick={onCancel} disabled={saving} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

function dayBefore(dateOnly) {
  const d = new Date(`${dateOnly}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

// Change the History start date. Once syncing has started it only moves
// earlier, and the next sync pulls in the emails between the new date and
// the old one, so a large history can come in a stretch at a time.
function HistoryStartForm({ mailbox, onSaved, onCancel }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const max = mailbox.sync_started ? dayBefore(mailbox.history_start_date) : todayInTenant();
  const [value, setValue] = useState(max);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put('/api/my-account/mailbox/history-start', { history_start_date: value });
      dismissError();
      onSaved(res.data.data, res.data.message);
    } catch (err) {
      showError(err, 'Could not change the History start date');
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 14 }}>
      <label style={LABEL_STYLE}>New History start date</label>
      <DateInput value={value} max={max} onChange={(e) => setValue(e.target.value)} style={{ width: 180 }} />
      <div style={HELP_STYLE}>
        {mailbox.sync_started
          ? 'Choose an earlier date. The next sync pulls in the emails from it up to the current date, so you can bring in older email a stretch at a time.'
          : 'The first sync goes back no further than this date.'}
      </div>
      <div style={{ marginTop: 12 }}>
        <SaveButton loading={saving} disabled={!value || value > max} />
        <CancelButton onClick={onCancel} disabled={saving} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

function ConnectedMailbox({ account, onChanged }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [loggedActivities, setLoggedActivities] = useState([]);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [changingHistory, setChangingHistory] = useState(false);
  const [changingFolders, setChangingFolders] = useState(false);
  const { mailbox } = account;
  const editing = changingHistory || changingPassword || changingFolders;

  // Opening a Change form clears the last Sync Now result, so what shows
  // next is always from the latest action.
  const startEditing = (open) => {
    setSyncResult(null);
    setLoggedActivities([]);
    open(true);
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setLoggedActivities([]);
    try {
      const res = await axios.post('/api/my-account/mailbox/sync');
      setSyncResult({ ok: res.data.ok, message: res.data.message });
      const activities = res.data.logged_activities || [];
      setLoggedActivities(activities);
      setResultsOpen(activities.length > 0);
      onChanged(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Sync failed');
    }
    setSyncing(false);
  };

  const handleDisconnect = async () => {
    if (!(await askConfirm(`Disconnect ${mailbox.email}? Syncing stops. Emails already logged from it stay on their records.`, { confirmLabel: 'Disconnect' }))) return;
    try {
      const res = await axios.delete('/api/my-account/mailbox');
      onChanged(res.data.data, res.data.message);
      dismissError();
    } catch (err) {
      showError(err, 'Disconnect failed');
    }
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 8, fontSize: 14 }}>
        <span style={{ color: '#5a6b85' }}>Mailbox</span>
        <span style={{ fontWeight: 600 }}>{mailbox.email}</span>
        <span style={{ color: '#5a6b85' }}>History from</span>
        <span>
          {formatDate(mailbox.history_start_date)}
          {!editing && (
            <button type="button" onClick={() => startEditing(setChangingHistory)} title="Change how far back this mailbox is synced" style={LINK_BUTTON}>
              Change
            </button>
          )}
        </span>
        <span style={{ color: '#5a6b85' }}>Folders</span>
        <span>
          {mailbox.folders_summary}
          {!editing && account.mail_server.ready && (
            <button type="button" onClick={() => startEditing(setChangingFolders)} title="Choose which folders of this mailbox are synced" style={LINK_BUTTON}>
              Change
            </button>
          )}
        </span>
        <span style={{ color: '#5a6b85' }}>Last sync</span>
        <span>{mailbox.last_synced_at ? formatDateTime(mailbox.last_synced_at) : 'Not yet'}</span>
        {account.mail_server.ready && (
          <>
            <span style={{ color: '#5a6b85' }}>Checked</span>
            <span>{frequencyLabel(account.mail_server.check_frequency_minutes)}</span>
          </>
        )}
      </div>
      {mailbox.backfill && (
        <div style={{ marginTop: 12, background: '#e8f0fd', border: '1px solid #bcd0f5', color: '#1e3a8a', borderRadius: 6, padding: '8px 12px', fontSize: 13 }}>
          The next sync pulls in emails from {formatDate(mailbox.backfill.since)} up to {formatDate(dayBefore(mailbox.backfill.before))}.
        </div>
      )}
      {mailbox.last_sync_error && (
        <div style={{ marginTop: 12, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 6, padding: '8px 12px', fontSize: 13 }}>
          <strong>Last sync failed:</strong> {mailbox.last_sync_error}
        </div>
      )}
      {!account.mail_server.ready && (
        <div style={{ marginTop: 12, background: '#fff3d6', border: '1px solid #f0d58c', color: '#8a5a00', borderRadius: 6, padding: '8px 12px', fontSize: 13 }}>
          {account.mail_server.message}
        </div>
      )}
      {syncResult && (
        <div style={{ marginTop: 12, fontSize: 13, color: syncResult.ok ? '#2e7d32' : '#b91c1c' }}>
          {syncResult.message}
          {loggedActivities.length > 0 && (
            <button
              type="button"
              onClick={() => setResultsOpen(true)}
              title="Show the email Activities this sync logged"
              style={{ marginLeft: 8, background: 'none', border: 'none', padding: 0, color: '#1d4ed8', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}
            >
              View
            </button>
          )}
        </div>
      )}
      <SyncResultsDrawer
        open={resultsOpen}
        onOpenChange={setResultsOpen}
        activities={loggedActivities}
        onRemoved={(removed, data, message) => {
          setLoggedActivities((list) => list.filter((a) => a.id !== removed.id));
          onChanged(data, message);
        }}
      />
      {changingFolders ? (
        <MailboxFolderPicker
          endpoint="/api/my-account/mailbox/folders"
          onSaved={(res) => { setChangingFolders(false); onChanged(res.data, res.message); }}
          onCancel={() => setChangingFolders(false)}
        />
      ) : changingHistory ? (
        <HistoryStartForm
          mailbox={mailbox}
          onSaved={(data, message) => { setChangingHistory(false); onChanged(data, message); }}
          onCancel={() => setChangingHistory(false)}
        />
      ) : changingPassword ? (
        <PasswordForm
          onSaved={(data, message) => { setChangingPassword(false); onChanged(data, message); }}
          onCancel={() => setChangingPassword(false)}
        />
      ) : (
        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleSync}
            disabled={syncing || !account.mail_server.ready}
            title="Check this mailbox for new email now"
            style={{ ...SECONDARY_BUTTON, cursor: (syncing || !account.mail_server.ready) ? 'not-allowed' : 'pointer', opacity: account.mail_server.ready ? 1 : 0.6 }}
          >
            {syncing && <Loader2 size={14} strokeWidth={2.5} style={{ animation: 'my-account-spin 0.8s linear infinite' }} />}
            {syncing ? 'Syncing...' : 'Sync Now'}
          </button>
          <button type="button" onClick={() => startEditing(setChangingPassword)} title="Replace the stored mailbox password" style={{ ...SECONDARY_BUTTON, cursor: 'pointer' }}>
            Update password
          </button>
          <button type="button" onClick={handleDisconnect} title="Stop syncing this mailbox" style={{ ...SECONDARY_BUTTON, color: '#b91c1c', cursor: 'pointer' }}>
            Disconnect
          </button>
        </div>
      )}
    </div>
  );
}

function NeverLogList({ entries, onChanged }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post('/api/my-account/never-log', { value });
      setValue('');
      onChanged(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Could not add it');
    }
    setSaving(false);
  };

  const handleRemove = async (entry) => {
    try {
      const res = await axios.delete(`/api/my-account/never-log/${entry.id}`);
      onChanged(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Could not remove it');
    }
  };

  return (
    <div>
      <p style={INTRO_STYLE}>
        An email in your mailbox is never logged if anyone on it (From, To or Cc) is on this list, even when it also involves a synced Customer. It only applies to your own mailbox. Add a full address (name@example.com) or a whole domain (example.com).
      </p>
      <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input className="styled-input" value={value} onChange={(e) => setValue(e.target.value)} placeholder="name@example.com or example.com" style={{ width: '100%', maxWidth: 300 }} />
        <SaveButton loading={saving} disabled={!value.trim()}>{saving ? 'Adding...' : 'Add'}</SaveButton>
      </form>
      {entries.length === 0 ? (
        <div style={{ marginTop: 12, fontSize: 13, color: '#888' }}>Nothing on your list yet.</div>
      ) : (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {entries.map((entry) => (
            <div key={entry.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', border: '1px solid #e3e7ee', borderRadius: 6, padding: '6px 10px', fontSize: 14 }}>
              <span>
                {entry.value}
                <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>{entry.value.includes('@') ? 'Address' : 'Domain'}</span>
              </span>
              <button
                type="button"
                onClick={() => handleRemove(entry)}
                title={`Remove ${entry.value} from your never-log list`}
                aria-label={`Remove ${entry.value}`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', display: 'inline-flex', padding: 2 }}
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MyAccountPage() {
  const showError = useErrorToast();
  const [account, setAccount] = useState(null);
  const [notice, setNotice] = useState('');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    axios.get('/api/my-account')
      .then((res) => setAccount(res.data.data))
      .catch((err) => {
        setLoadError(err.response?.data?.message || 'Could not load your account');
        showError(err, 'Could not load your account');
      });
    // eslint-disable-next-line
  }, []);

  const handleChanged = (data, message) => {
    if (data) setAccount(data);
    setNotice(message || '');
  };

  const container = { width: '100%', maxWidth: 600, margin: '32px auto 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 };

  if (!account) {
    return <div style={container}>{loadError || 'Loading...'}</div>;
  }

  return (
    <div style={container}>
      <style>{'@keyframes my-account-spin { to { transform: rotate(360deg); } }'}</style>
      <h2 style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 20 }}>My Account</h2>
      <p style={{ color: '#666', fontSize: 14, marginTop: 0 }}>{account.name} · {account.email}</p>

      {notice && (
        <div style={{ margin: '12px 0', background: '#e3f1e4', border: '1px solid #b7dbb9', color: '#1b5e20', borderRadius: 6, padding: '8px 12px', fontSize: 14 }}>
          {notice}
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <Accordion title="My Mailbox" showMenuPlaceholder={false}>
          {account.mailbox ? (
            <ConnectedMailbox account={account} onChanged={handleChanged} />
          ) : account.mail_server.ready ? (
            <ConnectForm account={account} onSaved={handleChanged} />
          ) : (
            <div style={{ fontSize: 13, color: '#8a5a00' }}>{account.mail_server.message}</div>
          )}
        </Accordion>

        <Accordion title="Never-Log List" showMenuPlaceholder={false}>
          <NeverLogList entries={account.never_log} onChanged={handleChanged} />
        </Accordion>
      </div>
    </div>
  );
}

export default MyAccountPage;
